import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { MenuItem } from 'primeng/api';
import { AppSettings } from './../../app.settings';
import { StorageService } from './../../services/storage.service';
import { SidebarService } from './../../services/sidebar.service';
import { Subscription } from 'rxjs';
import { CommonBindingDataService } from '../../services/common-binding-data.service';
import { UserCommonService } from '../../services/user-common.service';
import { NavigationEnd, Router } from '@angular/router';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html'
})
export class SidebarComponent implements OnInit, OnDestroy {
  items: MenuItem[];
  subscription: Subscription;
  isMultipleTabOpen: any = false;
  menuTransitionOptions = '900ms cubic-bezier(0.34, 1.45, 0.64, 1)';
  user;
  buildVersion;
  tenantInfo: any;
  poweredByRedirectUrl = AppSettings.DEFAULT_POWERED_BY_REDIRECT_URL;
  poweredLogoUrl = AppSettings.DEFAULT_POWERED_BY_LOGO_URL;
  logoPath = AppSettings.logoPath;
  logoSvg: SafeHtml | null = null;
  private readonly logoAssetPath = '/assets/images/MI/header-logo-transparent.svg';

  constructor(
    private storageService: StorageService,
    private sidebarService: SidebarService,
    private title: Title,
    private commonBindingDataService: CommonBindingDataService,
    public signInService: UserCommonService,
    private router: Router,
    private uiService: UiService,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.subscription = new Subscription();
    this.loadThemedLogo();
    this.uiService.initializeCollapsedSidebar();
    this.initSidebarItems();
    this.subscription.add(this.sidebarService.sideState.subscribe((state: any) => {
        this.updateItems(state);
      }));
    this.subscription.add(this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.manageActiveStatus();
      }
    }));

    this.commonBindingDataService.redrawSidebar.subscribe(() => {
      this.initSidebarItems();
    });
    this.tenantInfo = this.storageService.getItemFromSessionStorage(AppSettings.TENANT_INFO);
    if (this.tenantInfo && this.tenantInfo.poweredByName) {
      this.poweredByRedirectUrl = this.tenantInfo.poweredByName;
      
    }
    if (this.tenantInfo && this.tenantInfo.poweredByImageId) {
      this.poweredLogoUrl = AppSettings.GET_FILE_URL + '/' + this.tenantInfo.poweredByImageId;
    }
  }

  private loadThemedLogo(): void {
    this.subscription.add(
      this.http.get(this.logoAssetPath, { responseType: 'text' }).subscribe(
        (svg) => {
          // Keep asset file as source; inject inline so currentColor follows inactive icon color.
          const withAttrs = svg.replace(
            '<svg',
            '<svg role="img" aria-label="Mobisoft"'
          );
          this.logoSvg = this.sanitizer.bypassSecurityTrustHtml(withAttrs);
        },
        () => {
          this.logoSvg = null;
        }
      )
    );
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  initSidebarItems() {
    this.items = [];
    this.user = this.storageService.getItemFromSessionStorage(AppSettings.USER_DETAILS);
    if (this.items.length <= 0) {
      if (this.user !== null && this.user !== undefined) {
        this.items = this.getMenu();
        
      }
    }
    this.updateItems();
    this.manageActiveStatus();
    this.buildVersion = this.commonBindingDataService.getTenantConfigValue(AppSettings.WEB_VERSION);
  }

  updateItems(state?) {
    if (state) {
      this.items = state;
    }
    this.items = this.removeDuplicates(this.items, 'label');
  }

  removeDuplicates(myArr, prop) {
    return myArr.filter((obj, pos, arr) => {
      return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
    });
  }

  private manageActiveStatus() {
    const url = this.normalizeRoute(this.router.url);
    const selectedMenu = (localStorage.getItem('selectedMenu') || this.storageService.getItemFromSessionStorage('selectedMenu') || '').trim();
    const urlSection = this.getSectionRoute(url);

    this.items.forEach(item => {
      item.expanded = false;
      if (item?.items?.length) {
        item.expanded = item.items.some(subItem => {
          const route = this.normalizeRoute(subItem?.routerLink?.[0]);
          const sectionRoute = this.getSectionRoute(route);
          const matchesRoute = this.isRouteMatch(url, route);
          const matchesSection = !!urlSection && sectionRoute === urlSection;
          const matchesSelectedMenu = !!selectedMenu && subItem?.label === selectedMenu;

          return matchesRoute || matchesSection || matchesSelectedMenu;
        });
      }
    });
  }

  private normalizeRoute(route: string): string {
    if (!route) {
      return '';
    }

    const cleanedRoute = route.split('?')[0].split('#')[0].trim();
    if (!cleanedRoute) {
      return '';
    }

    const routeWithLeadingSlash = cleanedRoute.startsWith('/') ? cleanedRoute : `/${cleanedRoute}`;
    return routeWithLeadingSlash.endsWith('/') && routeWithLeadingSlash.length > 1
      ? routeWithLeadingSlash.slice(0, -1)
      : routeWithLeadingSlash;
  }

  private getSectionRoute(route: string): string {
    const routeSegments = route.split('/').filter(segment => !!segment);
    return routeSegments.length ? `/${routeSegments[0]}` : '';
  }

  private isRouteMatch(url: string, route: string): boolean {
    if (!url || !route) {
      return false;
    }

    return url === route || url.startsWith(`${route}/`);
  }

  selectedMenuClick(item) {
    const selectedMenu = item.srcElement.textContent.trim();
    this.storageService.setSessionStorage('selectedMenu', selectedMenu);
    localStorage.setItem('selectedMenu', selectedMenu);
  }

  getMenu() {
    return this.sidebarService.sideBarMenu;
  }

  initialiseSidebarMenu() {
    return [
      {
        label: 'Dashboard',
        title: 'Dashboard',
        icon: 'fa-dashboard-menu',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_dashboard'));
        },
        routerLink: [
          '/admin-dashboard'
        ],
        accessCode: 'DASHBOARD'
      },
      {
        label: 'Bookings',
        title: 'Bookings',
        icon: 'fa-booking-menu',
        accessCode: 'BOOKINGS',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_bookings'));
        },
        'items': [
          {
            label: 'Current Bookings',
            title: 'Current Bookings',
            icon: 'fa-current-booking',
            routerLink: [
              '/bookings/current-bookings'
            ],
          },
          {
            label: 'Booking History',
            title: 'Booking History',
            icon: 'fa-booking-history',
            routerLink: [
              '/bookings/booking-history'
            ],
          },
          {
            label: 'Load Bookings',
            title: 'Load Bookings',
            icon: 'fa-load-booking',
            routerLink: [
              '/bookings/load-bookings'
            ],
          },
        ]
      },
      {
        label: 'Emergency',
        title: 'Emergency',
        icon: 'fa-emergency-menu',
        routerLink: [
          '/emergency'
        ],
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_emergency_bookings'));
        },
      },
      {
        label: 'Manage Shifts',
        title: 'Manage Shifts',
        icon: 'fa-shift-menu',
        routerLink: [
          '/shift-management'
        ],
        accessCode: 'Shift Management',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_shift_management'));
        },
      },
      {
        label: 'Driver Management',
        title: 'Driver Management',
        icon: 'fa-driver-menu',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_driver_management'));
        },
        'items': [
          {
            label: 'Manage Drivers',
            title: 'Manage Drivers',
            icon: 'fa-manage-driver',
            routerLink: [
              '/drivers/driver-list'
            ],
          },
          {
            label: 'Driver Dashboard',
            title: 'Driver Dashboard',
            icon: 'fa-driver-dashboard',
            routerLink: [
              '/drivers/driver-dashboard'
            ],
          }
        ]
      },
      {
        label: 'Manage Passengers',
        title: 'Manage Passengers',
        icon: 'fa-passenger-menu',
        routerLink: [
          '/passengers'
        ],
        accessCode: 'PASSENGERS',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_manage_passengers'));
        }
      },
      {
        label: 'Manage Vehicles',
        title: 'Manage Vehicles',
        icon: 'fa-vehicle-menu',
        routerLink: [
          '/vehicles'
        ],
        accessCode: 'VEHICLES',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_vihicle_management'));
        }
      },
      {
        label: 'Manage Locations',
        title: 'Manage Locations',
        icon: 'fa-locations-menu',
        routerLink: [
          '/manage-locations'
        ],
        accessCode: 'Manage Locations',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_manage_locations'));
        }
      },
      {
        label: 'User Management',
        title: 'User Management',
        icon: 'fa-user-management-menu',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_user_management'));
        },
        'items': [
          {
            label: 'Manage Users',
            title: 'Manage Users',
            icon: 'fa-manage-users',
            routerLink: [
              'user-management/manage-users'
            ],
            accessCode: 'MANAGE_USER'
          },
          {
            label: 'Manage Roles',
            title: 'Manage Roles',
            icon: 'fa-manage-roles',
            routerLink: [
              '/user-management/manage-roles'
            ],
            accessCode: 'MANAGE_ROLE'
          }
        ]
      },
      {
        label: 'Broadcast Management',
        title: 'Broadcast Management',
        icon: 'fa-marketing-menu',
        command: (event) => {
          this.title.setTitle(this.commonBindingDataService.getLabel('title_broadcast_management'));
        },
        'items': [
          {
            label: 'Manage Notifications',
            title: 'Manage Notifications',
            icon: 'fa-manage-notification',
            routerLink: [
              '/marketing/notifications'
            ],
            accessCode: 'MARKETING_NOTIFICATIONS'
          },
          {
            label: 'Manage Segments',
            title: 'Manage Segments',
            icon: 'fa-manage-segment',
            routerLink: [
              '/marketing/segments'
            ],
            accessCode: 'MARKETING_SEGMENTS'
          }
        ]
      },
      {
        label: 'Reports',
        title: 'Reports',
        icon: 'fa-report-menu',
        'items': [
          {
            label: 'Driver Login Logout',
            title: 'Driver Login Logout',
            icon: 'fa-driver-login-logout-report',
            routerLink: [
              'reports/driver-login-logout'
            ],
            accessCode: 'DRIVER_LOGIN_LOGOUT'
          },
          {
            label: 'Driver Checkin Reports',
            title: 'Driver Checkin Reports',
            icon: 'fa-driver-checkin-report',
            routerLink: [
              '/reports/driver-checkin-reports'
            ],
            accessCode: 'DRIVER_LOGIN_LOGOUT'
          },
          {
            label: 'Driver On Duty',
            title: 'Driver On Duty',
            icon: 'fa-driver-on-duty-report',
            routerLink: [
              'reports/driver-duty'
            ],
            accessCode: 'DRIVER_ON_DUTY'
          },
          {
            label: 'Driver Performance',
            title: 'Driver Performance',
            icon: 'fa-driver-performace',
            routerLink: [
              'reports/driver-performance'
            ],
            accessCode: 'DIRVER_PERFORMANCE'
          },
          {
            label: 'Vehicle Mileage',
            title: 'Vehicle Mileage',
            icon: 'fa-car-mileage',
            routerLink: [
              'reports/car-mileage'
            ],
            accessCode: 'VEHICLE_MILEAGE'
          },
          {
            label: 'Passenger Feedback',
            title: 'Passenger Feedback',
            icon: 'fa-passenger-feedback',
            routerLink: [
              'reports/passenger-feedback'
            ],
            accessCode: 'PASSENGER_FEEDBACK'
          },
          {
            label: 'Passenger Bookings',
            title: 'Passenger Bookings',
            icon: 'fa-passenger-bookings',
            routerLink: [
              '/reports/passenger-bookings'
            ],
            accessCode: 'PASSENGER_BOOKINGS'
          },

        ]
      },
    ]
  }

  onSidebarMouseEnter() {
    if (!this.canToggleSidebarOnHover()) {
      return;
    }

    this.uiService.expandSidebarOnHover();
  }

  onSidebarMouseLeave() {
    if (!this.canToggleSidebarOnHover()) {
      return;
    }

    this.uiService.collapseSidebarOnHoverLeave();
  }

  private canToggleSidebarOnHover(): boolean {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth > 767;
  }
}